import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Stack,
  IconButton,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  styled,
  Button,
  Box,
  Tooltip,
  Menu,
  MenuItem,
  ListSubheader,
  Dialog,
  DialogContent,
  DialogActions,
  Alert,
  Drawer,
  List,
  ListItem,
  ListItemText,
  AppBar,
  Toolbar,
  useTheme,
} from "@mui/material";
import ReactEChart from "echarts-for-react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import BorderAllIcon from "@mui/icons-material/BorderAll";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { LTCPContext } from "LTCP Module/LTCPContext";
import {
  acceptCS,
  getProductTypeOfFormatL2,
  getRecommendationComparison,
  getRecommendationCrossSourcing,
  getRecommendationCrossSourcingNested,
  getRecommendationLineSpeed,
  getRecommendationLineSpeedOR,
  getRecommendationPrebuild,
  resetCS,
} from "LTCP Module/api/ltcpBackend";
import DemandCapacityGraph from "LTCP Module/components/DemandCapacityGraph";
import { interval } from "date-fns";
import { roundOffNumber } from "utils/roundOff";
import ltcpRoutes from "LTCP Module/constants/ltcpRoutes";
import { useMsal } from "@azure/msal-react";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PillBox from '../components/PillBox';

const MONTH_NAME = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// return the number of months as integer between the given two months
// as string value in mmm format.
function monthsBetweenInclusive(startDate, endDate) {
  const monthMap = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  };

  function parseDate(dateStr) {
    const [monthStr, yearStr] = dateStr.split("-");
    const month = monthMap[monthStr];
    const year = parseInt(yearStr, 10);
    return { year, month };
  }

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const totalMonthsStart = start.year * 12 + start.month;
  const totalMonthsEnd = end.year * 12 + end.month;

  const diff = totalMonthsEnd - totalMonthsStart;

  if (diff < 0) {
    throw new Error("End date must be after or equal to start date.");
  }

  return diff + 1;
}

// Styled component for table header with custom styles. We will reuse
// this component in multiple tables with same style.
const TableHeaderCell = styled(TableCell)({
  fontWeight: "bold",
  background: "#354acd",
  color:"#FFFFFF",
  textAlign: "center",
  py: 0,
  fontSize: 12,
});

// Styled component for table cell with custom styles. We will reuse this
// component in multiple tables with same style.
const TableDataCell = styled(TableCell)({
  fontSize: 12,
  textWrap: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid #ccc",
});

const InfoTableHeaderCell = styled(TableCell)({
  fontWeight: "bold",
  background: "#354acd",
  color:"#FFFFFF",
  textAlign: "center",
  py: 0,
  fontSize: 10,
});

const InfoTableDataCell = styled(TableCell)({
  fontSize: 9,
  // textWrap: "nowrap",
  // overflow: "hidden",
  // textOverflow: "ellipsis",
  borderBottom: "1px solid #ccc",
});

// This is a helper function to calculate the cu value based on the demand
// and capacity and for specific cased it return 'No Capacity' instead of cu
// value.
function getCUValue(demand, capacity, cu) {
  demand = roundOffNumber(demand);
  capacity = roundOffNumber(capacity);

  if (demand === 0 && capacity === 0) {
    return "0%";
  } else if (demand > 0 && capacity === 0) {
    return "No Capacity";
  } else {
    return roundOffNumber(cu) + "%";
  }
}

// This card component is used inside the cross sourcing page to show the
// alternative factories with the tooltip. It also fetch and show popup table
// with the detailed data of that alternative factory.
function CrossSourcingCard({ indexNum, text, onClick, allData }) {
  const roundData = (val) => {
    try {
      return roundOffNumber(val);
    } catch {
      return val;
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        width: "218px",
        minWidth: "218px",
        height: "50px",
        display: "flex",
        alignItems: "center",
        justifyContent: "start",
        gap: 2,
        px: 2,
        flexDirection: "row",
        borderRadius: "4px",
        position: "relative",
        background: "#7182ed",
      }}
    >
      <Box
        sx={{
          wdith: "32px",
          height: "32px",
          minWidth: "32px",
          maxWidth: "32px",
          minHeight: "32px",
          maxHeight: "32px",
          borderRadius: "16px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#F6F7FF",
        }}
      >
        <Typography fontWeight="bold" fontSize={18} color="#354acd">
          {indexNum}
        </Typography>
      </Box>
      <Typography fontSize={14} fontWeight="medium" color="#fff">
        {text}
      </Typography>
      <Tooltip
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              border: "2px solid #354acd",
              borderRadius: "12px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
              backgroundColor: "#D2E9F4", // your custom background
              color: "#000", // optional: text color
              fontSize: "14px", // optional
            },
          },
        }}
        title={
          <Stack color="#000">
            <Typography fontSize={11}>
              <b>Distance: </b>
              {roundData(allData.distance)} KM
            </Typography>
            <Typography fontSize={11}>
              <b>Available Capacity: </b>
              {roundData(allData.available_capacity)} T
            </Typography>
            <Typography fontSize={11}>
              <b>CU DNS: </b>
              {roundData(allData.cu)}%
            </Typography>
            <Typography fontSize={11}>
              <b>Used Capacity for Cross Sourcing: </b>
              {roundData(allData.cs)} T
            </Typography>
            <Typography fontSize={11}>
              <b>Brand Variants Produced: </b>
              {allData.brand_variants}
            </Typography>
          </Stack>
        }
      >
        <InfoOutlineIcon
          fontSize="small"
          sx={{ position: "absolute", top: 3, right: 3, color: "#fff" }}
        />
      </Tooltip>
    </Box>
  );
}

// This are the toggle table cell that we can enable or disable based on the
// business logic. We can also highlight these cells on row selection. We
// are using this component inside the cross sourcing page for access part.
function ToggleCell({ active, removed, children, sx }) {
  return (
    <TableDataCell
      sx={
        removed
          ? active
            ? { ...sx, color: "#888", background: "#7182ed" }
            : { ...sx, color: "#888", background: "#eee" }
          : active
          ? { ...sx, color: "#000", background: "#7182ed" }
          : { ...sx, color: "#888", background: "#fff" }
      }
    >
      {children}
    </TableDataCell>
  );
}

// This is a stateless table cell commponent that is used to edit and show
// values in table cell. On any change event it will call the given state
// set function and will update the state in the parent component.
function TableEditBox({ rowSpan, value, onChange }) {
  return (
    <TableDataCell rowSpan={rowSpan} sx={{ p: 0, background: "#e3e6f5" }}>
      <input
        type="number"
        value={value}
        onChange={onChange}
        style={{
          border: "none",
          background: "none",
          width: "100%",
          height: "100%",
          outline: "none",
          textAlign: "center",
          boxSizing: "border-box",
          fontSize: 12,
        }}
      />
    </TableDataCell>
  );
}

// This page will show the comparison between the prebuild, cross sourcing
// and line speed / line oee data. Line oee data will be collected from the
// user saved data.
function Comparison({ factory, format, oeeData }) {
  const { setLoading, demandFile, runtime, runtype,thresholdLevel,productType } = useContext(LTCPContext);
  let threshold =   productType !== "All"? thresholdLevel[productType]: 90

  const [allData, setAllData] = useState({
    summary: [],
    detailed: [],
  });

  async function fetchData() {
    setLoading(true);

    const resp = await getRecommendationComparison({
      demand_file: demandFile,
      runtime,
      runtype,
      format,
      factory,
    });

    resp.summary[0].cu_oee = oeeData.cu_oee || 0;

    resp.detailed = resp.detailed
      .map((item) => {
        let month = item.month.split("-");

        let sortKey = month[1] + "-" + month[0];
        month = MONTH_NAME[Number.parseInt(month[0]) - 1] + "-" + month[1];

        item.sortKey = sortKey;
        item.month = month;

        return item;
      })
      .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    setAllData(resp);
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const option1 = {
    grid: {
      top: 10,
      left: 50,
      right: 50,
      bottom: 50,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
    },
    legend: {
      data: ["Capacity Unlocked (T)", "CU"],
      orient: "horizontal",
      bottom: 0,
      textStyle: {
        fontSize: 8,
      },
      icon:"rect",
      itemWidth: 6,
      itemHeight: 15,
    },
    xAxis: [
      {
        type: "category",
        data: [
          "DNS",
          "After Prebuild",
          "After Cross Sourcing",
          "After Line Speed/OEE Increase",
        ],
        axisPointer: {
          type: "shadow",
        },
        axisLabel: {
          interval: 0,
          fontSize: 8,
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "Capacity Unlocked (T)",
        splitLine: {
          show: false,
        },
        axisLabel: {
          formatter: "{value}",
          fontSize: 10,
        },
      },
      {
        type: "value",
        name: "CU",
        axisLabel: {
          formatter: "{value}%",
          fontSize: 10,
        },
      },
    ],
    series: [
      {
        name: "Capacity Unlocked (T)",
        type: "bar",
        data: [
          0,
          roundOffNumber(allData.summary[0]?.cap_prebuild || 0),
          roundOffNumber(allData.summary[0]?.cap_cs || 0),
          roundOffNumber(oeeData.cap_increase),
        ],
        itemStyle: {
          color: "#0055ee",
          borderRadius: [3, 3, 0, 0],
        },
      },
      {
        name: "CU",
        type: "line",
        yAxisIndex: 1,
        tooltip: {
          valueFormatter: function (value) {
            return value + "%";
          },
        },
        data: [
          roundOffNumber(allData.summary[0]?.cu_dns || 0),
          roundOffNumber(allData.summary[0]?.cu_prebuild || 0),
          roundOffNumber(allData.summary[0]?.cu_cs || 0),
          roundOffNumber(allData.summary[0]?.cu_oee || 0),
        ],
        itemStyle:{
          color: "#FF9E00",
        },
        lineStyle: {
          color: "#FF9E00",
          width: 1,
        },
        smooth: true,
      },
    ],
  };

  const option2 = {
    grid: {
      top: 10,
      left: 50,
      right: 50,
      bottom: 50,
    },
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
    },
    legend: {
      data: [
        "CU % (DNS)",
        "CU% After Prebuild",
        "CU% After Crossing Source",
        "CU% After Line Speed / OEE increase",
      ],
      orient: "horizontal",
      bottom: 0,
      textStyle: {
        fontSize: 8,
      },
      icon:"rect",
      itemWidth: 6,
      itemHeight: 15,
    },
    xAxis: [
      {
        type: "category",
        data: allData.detailed.map((item) => item.month),
        axisPointer: {
          type: "shadow",
        },
        axisLabel: {
          fontSize: 8,
        },
      },
    ],
    yAxis: [
      {
        type: "value",
        name: "Capacity Unlocked (T)",
        axisLabel: {
          formatter: "{value}%",
          fontSize: 10,
        },
      },
    ],
    series: [
      {
        name: "CU % (DNS)",
        type: "line",
        tooltip: {
          valueFormatter: function (value) {
            return value + "%";
          },
        },
        data: allData.detailed.map((item) => roundOffNumber(item.cu_dns)),
        itemStyle:{
          color: "#FF9E00",
        },
        lineStyle: {
          color: "#FF9E00",
          width: 1,
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
      },
      {
        name: "CU% After Prebuild",
        type: "line",
        tooltip: {
          valueFormatter: function (value) {
            return value + "%";
          },
        },
        data: allData.detailed.map((item) => roundOffNumber(item.cu_prebuild)),
        itemStyle:{
          color: "#1F36C7",
        },
        lineStyle: {
          color: "#1F36C7",
          width: 1,
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
      },
      {
        name: "CU% After Crossing Source",
        type: "line",
        tooltip: {
          valueFormatter: function (value) {
            return value + "%";
          },
        },
        data: allData.detailed.map((item) => roundOffNumber(item.cu_cs)),
        itemStyle:{
          color: "#ff0027",
        },
        lineStyle: {
          color: "#ff0027",
          width: 1,
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
      },
      {
        name: "CU% After Line Speed / OEE increase",
        type: "line",
        tooltip: {
          valueFormatter: function (value) {
            return value + "%";
          },
        },
        data: oeeData.months.map((item) => item.cu),
                itemStyle:{
          color: "#009a6e",
        },
        lineStyle: {
          color: "#009a6e",
          width: 1,
        },
        smooth: true,
        symbol: "circle",
        symbolSize: 7,
      },
    ],
  };

  return (
    <Stack gap={2}>
      <Paper sx={{ overflowY: "auto", height: "calc(22vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5}>
          <Typography fontWeight="bold" fontSize={14}>
            Summary
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Alert Type</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Month</TableHeaderCell>
                  <TableHeaderCell>CU% DNS</TableHeaderCell>
                  <TableHeaderCell>CU% After Prebuild</TableHeaderCell>
                  <TableHeaderCell>CU% After Cross sourcing</TableHeaderCell>
                  <TableHeaderCell>
                    CU% After Line Speed/OEE Increase
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allData.summary.map((item, index) => (
                  <TableRow key={index}>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.factory}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.format}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.period}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_dns)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_dns >= threshold || getCUValue(item.demand, item.capacity, item.cu_dns) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_prebuild)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_prebuild >= threshold || getCUValue(item.demand, item.capacity, item.cu_prebuild) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_cs)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_cs >= threshold || getCUValue(item.demand, item.capacity, item.cu_cs) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_oee)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_oee >= threshold || getCUValue(item.demand, item.capacity, item.cu_oee) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Paper sx={{ height: "calc(38vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5} height="98%">
          <Typography fontWeight="bold" fontSize={14}>
            Comparison Detail
          </Typography>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Factory</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Month</TableHeaderCell>
                  <TableHeaderCell>CU% DNS</TableHeaderCell>
                  <TableHeaderCell>CU% After Prebuild</TableHeaderCell>
                  <TableHeaderCell>CU% After Cross sourcing</TableHeaderCell>
                  <TableHeaderCell>
                    CU% After Line Speed/OEE Increase
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allData.detailed.map((item, index) => (
                  <TableRow key={index}>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.factory}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.format}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.month}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_dns)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_dns >= threshold || getCUValue(item.demand, item.capacity, item.cu_dns) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                       <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_prebuild)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_prebuild >= threshold || getCUValue(item.demand, item.capacity, item.cu_prebuild) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_cs)}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_cs >= threshold || getCUValue(item.demand, item.capacity, item.cu_cs) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(
                        item.demand,
                        item.capacity,
                        oeeData.months.filter(
                          (data) => data.label === item.month
                        )[0]?.cu || 0
                      )}`} bgColor = { `${runtype.toLowerCase() !== "schedule" && ((oeeData.months.filter(
                          (data) => data.label === item.month
                        )[0]?.cu || 0) >= threshold || getCUValue(item.demand, item.capacity, oeeData.months.filter(
                          (data) => data.label === item.month
                        )[0]?.cu || 0) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Stack
        direction="row"
        sx={{
          height: "calc(36vh - 50px)",
          gap: 2,
          pt: 0.2,
        }}
      >
        <Paper
          sx={{
            width: "50%",
            height: "100%",
            display: "flex",
          }}
        >
          <Stack sx={{ heigth: "100%", flexGrow: 1 }}>
            <Typography sx={{ width: "100%", textAlign: "center" }}>
              CU & Capacity Unlocked Comparison
            </Typography>
            <Box sx={{ display: "flex", flexGrow: 1 }}>
              <ReactEChart
                style={{ width: "100%", height: "100%" }}
                option={option1}
                notMerge
              />
            </Box>
          </Stack>
        </Paper>
        <Paper
          sx={{
            width: "50%",
            height: "100%",
            display: "flex",
          }}
        >
          <Stack sx={{ heigth: "100%", flexGrow: 1 }}>
            <Typography sx={{ width: "100%", textAlign: "center" }}>
              CU Comparison - MoM
            </Typography>
            <Box sx={{ display: "flex", flexGrow: 1 }}>
              <ReactEChart
                style={{ width: "100%", height: "100%" }}
                option={option2}
                notMerge
              />
            </Box>
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
}

// This is the simulation table row component that contain all the logics
// and based the the business rule it update the current and all the other
// related rows when user modify the BCT or line speed or line OEE.
function LineSpeedRow({
  item,
  format,
  currentProductType,
  currentIndex,
  allData,
  setAllData,
  selectedColumns,
  isMakes
}) {
  const productType = currentProductType;

  let activerow = format === item.formatsl2;
  let background = activerow ? "#b4bfff" : "none";

  let tmpSplit = item.capacitymonth.split("-");
  let month = Number.parseInt(tmpSplit[0]);
  let monthName = MONTH_NAME[month - 1] + "-" + tmpSplit[1];
  let hasMakeShiftsValue = item.makeshifts > 0;
  function changeBCT(val) {
    let newData = [...allData];

    for (let i = 0; i < allData.length; i++) {
      let thisRow = allData[i];

      const rowMatched =
        thisRow.brand === item.brand &&
        thisRow.formatsl1 === item.formatsl1 &&
        thisRow.mixerno === item.mixerno &&
        thisRow.cascadeno === item.cascadeno &&
        thisRow.brandvariant === item.brandvariant;

      if (!rowMatched) continue;

      let makecap =
        (480 / val) *
        thisRow.batchsize *
        thisRow.makeshifts *
        (thisRow.ope / 100);
      let act_capacity = Math.min(makecap, thisRow.group_sum);
      //TODO:
      // if (productType === "HC_LIQUIDS") {
      //   act_capacity = thisRow.group_sum;
      // }
      console.log('item value',item);
      newData[i].bct = val;
      newData[i].makecapacitytpw = makecap;
      newData[i].act_capacity = act_capacity;
      newData[i].act_pack_cap =
        (thisRow.capacitytpw * act_capacity) / thisRow.group_sum || 0;

      // if (productType === "HC_LIQUIDS") {
      //   newData[i].act_pack_cap = thisRow.capacitytpw;
      // }
    }

    setAllData(newData);
  }

  function changeLineSpeed(val) {
    let newData = [...allData];

    for (let i = 0; i < allData.length; i++) {
      let thisRow = allData[i];

      const rowMatched =
        thisRow.brand === item.brand &&
        thisRow.formatsl1 === item.formatsl1 &&
        thisRow.mixerno === item.mixerno &&
        thisRow.cascadeno === item.cascadeno &&
        thisRow.brandvariant === item.brandvariant &&
        thisRow.basepackdescription === item.basepackdescription &&
        thisRow.uniqueline === item.uniqueline;

      if (!rowMatched) continue;

      let capacitytpw =
        thisRow.packshifts *
        thisRow.grammage *
        thisRow.nooflines *
        val *
        thisRow.lineoee *
        (480 / Math.pow(10, 8));

      let group_sum = thisRow.group_sum - thisRow.capacitytpw + capacitytpw;
      let act_capacity = Math.min(thisRow.makecapacitytpw, group_sum);

      // if (productType === "HC_LIQUIDS") {
      //   act_capacity = group_sum;
      // }

      newData[i].linespeed = val;
      newData[i].capacitytpw = capacitytpw;

      for (let j = 0; j < newData.length; j++) {
        let tmpRow = newData[j];

        if (
          tmpRow.brand === thisRow.brand &&
          tmpRow.formatsl1 === thisRow.formatsl1 &&
          tmpRow.brandvariant === thisRow.brandvariant &&
          tmpRow.cascadeno == thisRow.cascadeno &&
          tmpRow.mixerno === thisRow.mixerno
        ) {
          newData[i].group_sum = group_sum;
          newData[i].act_capacity = act_capacity;
          console.log();;
          if(!item.makeshifts && item.makeshifts <= 0){
            newData[i].act_pack_cap  = newData[i].capacitytpw;
           }
           else {
           newData[i].act_pack_cap = (thisRow.capacitytpw * act_capacity) / thisRow.group_sum || 0;
          }

          // if (productType === "HC_LIQUIDS") {
          //   newData[i].act_pack_cap = newData[i].capacitytpw;
          // }
        }
      }
    }

    setAllData(newData);
  }

  function changeLineOee(val) {
    let newData = [...allData];

    for (let i = 0; i < allData.length; i++) {
      let thisRow = allData[i];

      const rowMatched =
        thisRow.brand === item.brand &&
        thisRow.formatsl1 === item.formatsl1 &&
        thisRow.mixerno === item.mixerno &&
        thisRow.cascadeno === item.cascadeno &&
        thisRow.brandvariant === item.brandvariant &&
        thisRow.basepackdescription === item.basepackdescription &&
        thisRow.uniqueline === item.uniqueline;

      if (!rowMatched) continue;

      let capacitytpw =
        thisRow.packshifts *
        thisRow.grammage *
        thisRow.nooflines *
        thisRow.linespeed *
        val *
        (480 / Math.pow(10, 8));

      let group_sum = thisRow.group_sum - thisRow.capacitytpw + capacitytpw;
      let act_capacity = Math.min(thisRow.makecapacitytpw, group_sum);

      // if (productType === "HC_LIQUIDS") {
      //   act_capacity = group_sum;
      // }

      newData[i].lineoee = val;
      newData[i].capacitytpw = capacitytpw;

      for (let j = 0; j < newData.length; j++) {
        let tmpRow = newData[j];

        if (
          tmpRow.brand === thisRow.brand &&
          tmpRow.formatsl1 === thisRow.formatsl1 &&
          tmpRow.brandvariant === thisRow.brandvariant &&
          tmpRow.cascadeno == thisRow.cascadeno &&
          tmpRow.mixerno === thisRow.mixerno
        ) {
          newData[i].group_sum = group_sum;
          newData[i].act_capacity = act_capacity;
          if(!item.makeshifts && item.makeshifts <= 0){
             newData[i].act_pack_cap  = newData[i].capacitytpw;
          }
           else {
             newData[i].act_pack_cap = (thisRow.capacitytpw * act_capacity) / thisRow.group_sum || 0;
          }
          // if (productType === "HC_LIQUIDS") {
          //   newData[i].act_pack_cap = newData[i].capacitytpw;
          // }
        }
      }
    }

    setAllData(newData);
  }

  return (
    <TableRow>
      {selectedColumns.factory && (
        <TableDataCell sx={{ background }}>{item.factoryname}</TableDataCell>
      )}
      {selectedColumns.brandvariant && (
        <TableDataCell sx={{ background }}>{item.brandvariant}</TableDataCell>
      )}
      {selectedColumns.format && (
        <TableDataCell sx={{ background }}>{item.formatsl2}</TableDataCell>
      )}
      {selectedColumns.basepack && (
        <TableDataCell sx={{ background }}>
          {item.basepackdescription}
        </TableDataCell>
      )}
      {isMakes && selectedColumns.cascade && (
        <TableDataCell sx={{ background }}>{item.cascadeno}</TableDataCell>
      )}
      {isMakes && selectedColumns.mixer && (
        <TableDataCell sx={{ background }}>{item.mixerno}</TableDataCell>
      )}
      {item.key_count > 0 && (
        <>
          {isMakes && selectedColumns.makeshift && (
            <TableDataCell
              rowSpan={item.key_count}
              sx={{ textAlign: "center" }}
            >
              {item.makeshifts}
            </TableDataCell>
          )}
          {isMakes && selectedColumns.batchsize && (
            <TableDataCell
              rowSpan={item.key_count}
              sx={{ textAlign: "center" }}
            >
              {item.batchsize}
            </TableDataCell>
          )}
          {isMakes && selectedColumns.bct &&
            (activerow && hasMakeShiftsValue ? (
              <TableEditBox
                rowSpan={item.key_count}
                sx={{ textAlign: "center" }}
                value={item.bct}
                onChange={(e) => changeBCT(e.target.value)}
              />
            ) : (
              <TableDataCell
                rowSpan={item.key_count}
                sx={{ textAlign: "center" }}
              >
                {item.bct}
              </TableDataCell>
            ))}
          {isMakes && selectedColumns.benchmarkbct && (
            <TableDataCell
              rowSpan={item.key_count}
              sx={{ textAlign: "center" }}
            >
              {item.benchmarkbct}
            </TableDataCell>
          )}
          {isMakes && selectedColumns.makecap && (
            <TableDataCell
              rowSpan={item.key_count}
              sx={{ textAlign: "center" }}
            >
              {roundOffNumber(item.makecapacitytpw)}
            </TableDataCell>
          )}
        </>
      )}
      {selectedColumns.machine && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {item.machine}
        </TableDataCell>
      )}
      {selectedColumns.uniqueline && (
        <TableDataCell sx={{ background }}>{item.uniqueline}</TableDataCell>
      )}
      {selectedColumns.packshift && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {item.packshifts}
        </TableDataCell>
      )}
      {selectedColumns.grammage && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {item.grammage}
        </TableDataCell>
      )}
      {selectedColumns.linespeed &&
        (activerow ? (
          <TableEditBox
            value={item.linespeed}
            onChange={(e) => changeLineSpeed(e.target.value)}
          />
        ) : (
          <TableDataCell sx={{ background, textAlign: "center" }}>
            {item.linespeed}
          </TableDataCell>
        ))}
      {selectedColumns.benchmarklinespeed && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {item.benchmarklinespeed}
        </TableDataCell>
      )}
      {selectedColumns.lineoee &&
        (activerow ? (
          <TableEditBox
            value={item.lineoee}
            onChange={(e) => changeLineOee(e.target.value)}
          />
        ) : (
          <TableDataCell sx={{ background, textAlign: "center" }}>
            {item.lineoee}
          </TableDataCell>
        ))}
      {selectedColumns.benchmarklineoee && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {item.benchmarklineoee}
        </TableDataCell>
      )}
      {selectedColumns.packcap && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {roundOffNumber(item.capacitytpw)}
        </TableDataCell>
      )}
      {isMakes &&
        selectedColumns.actcap &&
        item.key_count > 0 && (
          <TableDataCell rowSpan={item.key_count} sx={{ textAlign: "center" }}>
            {roundOffNumber(item.act_capacity)}
          </TableDataCell>
        )}
      {selectedColumns.actpackcap && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {roundOffNumber(item.act_pack_cap)}
        </TableDataCell>
      )}
      {selectedColumns.month && (
        <TableDataCell sx={{ background, textAlign: "center" }}>
          {monthName}
        </TableDataCell>
      )}
    </TableRow>
  );
}

// This is the line speed page that showing the line speed data using the
// line speed row component. It also contain the save logic and after saving
// the data it will redirect the user to the comparison page.
function LineSpeed({ factory, format, setOeeData, setTableLevel }) {
  const {
    setLoading,
    demandFile,
    runtime,
    runtype,
    thresholdLevel,
    productType,
    setAlertWidget,
  } = useContext(LTCPContext);
  let threshold =   productType !== "All"? thresholdLevel[productType]: 90

  function getCurrentMonthYear() {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const year = now.getFullYear();
    return `${month}-${year}`;
  }

  const currentMonth = getCurrentMonthYear();

  const [columnSelector, setColumnSelector] = useState(null);
  const [selectAll, setSelectAll] = useState(true);
  const [currentProductType, setCurrentProductType] = useState(productType);
  const [selectedColumns, setSelectedColumns] = useState({
    factory: true,
    brandvariant: true,
    format: true,
    basepack: true,
    cascade: true,
    mixer: true,
    makeshift: true,
    batchsize: true,
    bct: true,
    benchmarkbct: true,
    makecap: true,
    machine: true,
    uniqueline: true,
    packshift: true,
    grammage: true,
    linespeed: true,
    benchmarklinespeed: true,
    lineoee: true,
    benchmarklineoee: true,
    packcap: true,
    actcap: true,
    actpackcap: true,
    month: false,
  });
  const [allData, setAllData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [orValue, setOrValue] = useState(1);
  const [prebuildData, setPrebuildData] = useState({
    alert: "",
    format: "",
    period: "",
    demand: 0,
    capacity: 0,
    cu: 0,
  });
  const [otherData, setOtherData] = useState({
    cap_increase: 0,
    new_cap: 0,
    new_cu: 0,
  });

  useEffect(() => {
    let totalCap = allData
      .filter((item) => item.formatsl2 === format)
      .reduce((acc, curr) => {
        acc += curr.act_pack_cap;

        return acc;
      }, 0);

    totalCap *= 4.17;

    totalCap = roundOffNumber(totalCap);

    setOtherData({
      new_cap: totalCap,
      cap_increase: totalCap - prebuildData.capacity,
      new_cu: (prebuildData.demand / (totalCap * (orValue / 100))) * 100,
    });

    const group = allData
      .filter((item) => item.formatsl2 === format)
      .reduce((acc, curr) => {
        const key = curr.capacitymonth + curr.demand;

        if (!acc[key]) {
          acc[key] = {
            label: curr.capacitymonth,
            demand: curr.demand,
            capacity: 0,
          };
        }

        acc[key].capacity += curr.act_pack_cap;

        return acc;
      }, {});

    let group_data = Object.values(group).reduce((acc, curr) => {
      const key = curr.label;

      if (!acc[key]) {
        acc[key] = { label: key, demand: 0, capacity: 0 };
      }

      acc[key].demand += curr.demand;
      acc[key].capacity += curr.capacity;

      return acc;
    }, {});

    group_data = Object.values(group_data)
      .map((item) => {
        let tmpSplit = item.label.split("-");

        let month = Number.parseInt(tmpSplit[0]);
        month = MONTH_NAME[month - 1];

        let demandValue = roundOffNumber(item.demand);
        let capacityValue = roundOffNumber(item.capacity * 4.17);

        let cuValue =
          roundOffNumber(
            (item.demand / (item.capacity * 4.17 * (orValue / 100))) * 100
          ) || 0;

        return {
          date: tmpSplit[1] + "-" + tmpSplit[0],
          label: month + "-" + tmpSplit[1],
          demand: demandValue,
          capacity: capacityValue,
          cu: cuValue,
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));

    setGraphData(group_data);
  }, [allData]);

  async function fetchData() {
    setLoading(true);

    let factory_code = factory.split(":");
    if (factory_code.length > 1) {
      factory_code = factory_code[1];
    } else {
      factory_code = factory_code[0];
    }

    if (productType === "All") {
      const resp = await getProductTypeOfFormatL2(format);
      setCurrentProductType(resp.producttype);
    }

    const resp = await Promise.all([
      getRecommendationLineSpeed({
        demand_file: demandFile,
        factory: factory_code,
        runtime,
        runtype,
        format,
      }),
      getRecommendationLineSpeedOR({
        demand_file: demandFile,
        factory: factory_code,
        runtime,
        runtype,
        format,
      }),
      getRecommendationPrebuild({
        factory,
        format,
        demandFile,
        runtime,
        runtype,
      }),
    ]);

    const prebuildData = resp[2].duration[0];

    const demandValue = roundOffNumber(prebuildData.demand);
    const capacityValue = roundOffNumber(prebuildData.capacity);

    prebuildData.demand = demandValue;
    prebuildData.capacity = capacityValue;

    // if (demandValue === 0 && capacityValue === 0) {
    //   prebuildData.cu = "0%";
    // } else if (capacityValue === 0) {
    //   prebuildData.cu = "No Capacity";
    // } else {
    //   prebuildData.cu = roundOffNumber(prebuildData.cu) + "%";
    // }

    setAllData(resp[0]);
    setOrValue(resp[1].or);
    setPrebuildData(prebuildData);

    setLoading(false);
  }
   function checkMakesOrNot(){
    return allData.every(
      row => row.cascadeno && row.cascadeno.trim().toLowerCase() !== "none" && row.mixerno && row.mixerno.trim().toLowerCase() !== "none"
    );
  }
  const isMakes = runtype.toLowerCase() === 'manual' ? checkMakesOrNot(): true;
  useEffect(() => {
    fetchData();
  }, []);

  function ColumnSelector({ name, title }) {
    return (
      <MenuItem
        onClick={() => {
          setSelectedColumns({
            ...selectedColumns,
            [name]: !selectedColumns[name],
          });
        }}
      >
        {selectedColumns[name] ? (
          <CheckBoxIcon sx={{ mr: 1, fontSize: 18, color: "blue" }} />
        ) : (
          <CheckBoxOutlineBlankIcon
            sx={{ mr: 1, fontSize: 18, color: "#888" }}
          />
        )}
        {title}
      </MenuItem>
    );
  }
// function checkMakesOrNot(){
//     return allData.every(
//       row => row.cascadeno && row.cascadeno.trim().toLowerCase() !== "none" && row.mixerno && row.mixerno.trim().toLowerCase() !== "none"
//     );
//   }
//    const isMakes = runtype.toLowerCase() === 'manual' ? checkMakesOrNot(): true;
  return (
    <Stack gap={2}>
      <Paper sx={{ overflowY: "auto", height: "calc(22vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5}>
          <Typography fontWeight="bold" fontSize={14}>
            Summary
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Alert Type</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Period</TableHeaderCell>
                  <TableHeaderCell>Demand (T)</TableHeaderCell>
                  <TableHeaderCell>Capacity (T)</TableHeaderCell>
                  <TableHeaderCell>CU%</TableHeaderCell>
                  <TableHeaderCell>
                    Potential Capacity Increase (T)
                  </TableHeaderCell>
                  <TableHeaderCell>
                    CU After Line Speed/OEE Increase
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {prebuildData.alert}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {prebuildData.format}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {prebuildData.period}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {prebuildData.demand}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {prebuildData.capacity}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    <PillBox value={getCUValue(prebuildData.demand, prebuildData.capacity, prebuildData.cu)} bgColor = { `${runtype.toLowerCase() !== "schedule" && (prebuildData.cu >= threshold || getCUValue(prebuildData.demand, prebuildData.capacity, prebuildData.cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    {roundOffNumber(otherData.cap_increase)}
                  </TableDataCell>
                  <TableDataCell sx={{ textAlign: "center" }}>
                    <PillBox value={getCUValue(prebuildData.demand, prebuildData.capacity, otherData.new_cu)} bgColor = { `${runtype.toLowerCase() !== "schedule" && (otherData.new_cu >= threshold || getCUValue(prebuildData.demand, prebuildData.capacity, otherData.new_cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                  </TableDataCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Paper sx={{ height: "calc(38vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5} height="96%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontWeight="bold" fontSize={14}>
              Line Speed/OEE Increase - Simulation
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setColumnSelector(e.currentTarget)}
            >
              <BorderAllIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </Stack>
          <Menu
            open={Boolean(columnSelector)}
            onClose={() => setColumnSelector(null)}
            anchorEl={columnSelector}
          >
            <ListSubheader>Select Columns</ListSubheader>
            <MenuItem
              onClick={() => {
                if (selectAll)
                  setSelectedColumns({
                    factory: true,
                    brandvariant: true,
                    format: true,
                    basepack: true,
                    cascade: true,
                    mixer: true,
                    makeshift: true,
                    batchsize: true,
                    bct: true,
                    benchmarkbct: true,
                    makecap: true,
                    machine: true,
                    uniqueline: true,
                    packshift: true,
                    grammage: true,
                    linespeed: true,
                    benchmarklinespeed: true,
                    lineoee: true,
                    benchmarklineoee: true,
                    packcap: true,
                    actcap: true,
                    actpackcap: true,
                    month: true,
                  });
                else
                  setSelectedColumns({
                    factory: false,
                    brandvariant: false,
                    format: false,
                    basepack: false,
                    cascade: false,
                    mixer: false,
                    makeshift: false,
                    batchsize: false,
                    bct: false,
                    benchmarkbct: false,
                    makecap: false,
                    machine: false,
                    uniqueline: false,
                    packshift: false,
                    grammage: false,
                    linespeed: false,
                    benchmarklinespeed: false,
                    lineoee: false,
                    benchmarklineoee: false,
                    packcap: false,
                    actcap: false,
                    actpackcap: false,
                    month: false,
                  });
                setSelectAll(!selectAll);
              }}
            >
              {selectAll ? (
                <CheckBoxOutlineBlankIcon
                  sx={{ mr: 1, fontSize: 18, color: "#888" }}
                />
              ) : (
                <CheckBoxIcon sx={{ mr: 1, fontSize: 18, color: "blue" }} />
              )}
              Select All
            </MenuItem>
            <ColumnSelector name="factory" title="Factory" />
            <ColumnSelector name="brandvariant" title="Brand Variant" />
            <ColumnSelector name="format" title="Formats L2" />
            <ColumnSelector name="basepack" title="Basepack Description" />
            {isMakes &&(
              <>
               <ColumnSelector name="cascade" title="Cascade No" />
               <ColumnSelector name="mixer" title="Mixer No" />
               <ColumnSelector name="makeshift" title="Make Shifts" />
               <ColumnSelector name="batchsize" title="Batch Size" />
               <ColumnSelector name="bct" title="BCT" />
               <ColumnSelector name="benchmarkbct" title="Benchmark BCT" />
               <ColumnSelector name="makecap" title="Make Cap (tpw)" />
              </>)
            }
            <ColumnSelector name="machine" title="Machine" />
            <ColumnSelector name="uniqueline" title="Unique Line" />
            <ColumnSelector name="packshift" title="Pack Shifts" />
            <ColumnSelector name="grammage" title="Grammage" />
            <ColumnSelector name="linespeed" title="Line Speed" />
            <ColumnSelector
              name="benchmarklinespeed"
              title="Benchmark Line Speed"
            />
            <ColumnSelector name="lineoee" title="Line OEE %" />
            <ColumnSelector
              name="benchmarklineoee"
              title="Benchmark Line OEE"
            />
            <ColumnSelector name="packcap" title="Pack Cap (tpw)" />
            {isMakes && (
              <ColumnSelector
                name="actcap"
                title="Act Capacity (Min of Make & Pack)"
              />
            )}
            <ColumnSelector name="actpackcap" title="Act Pack Cap (tpw)" />
            <ColumnSelector name="month" title="Month" />
          </Menu>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {selectedColumns.factory && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Factory
                    </TableHeaderCell>
                  )}
                  {selectedColumns.brandvariant && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Brand Variant
                    </TableHeaderCell>
                  )}
                  {selectedColumns.format && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Formats L2
                    </TableHeaderCell>
                  )}
                  {selectedColumns.basepack && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Basepack Description
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.cascade && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Cascade No
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.mixer && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Mixer No
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.makeshift && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Make Shifts
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.batchsize && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Batch Size
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.bct && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      BCT
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.benchmarkbct && (
                    <TableHeaderCell
                      sx={{ textWrap: "nowrap", background: "#00b190" }}
                    >
                      Benchmark BCT
                    </TableHeaderCell>
                  )}
                  {isMakes && selectedColumns.makecap && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Make Cap (tpw)
                    </TableHeaderCell>
                  )}
                  {selectedColumns.machine && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Machine
                    </TableHeaderCell>
                  )}
                  {selectedColumns.uniqueline && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Unique Line
                    </TableHeaderCell>
                  )}
                  {selectedColumns.packshift && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Pack Shifts
                    </TableHeaderCell>
                  )}
                  {selectedColumns.grammage && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Grammage
                    </TableHeaderCell>
                  )}
                  {selectedColumns.linespeed && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Line Speed
                    </TableHeaderCell>
                  )}
                  {selectedColumns.benchmarklinespeed && (
                    <TableHeaderCell
                      sx={{ textWrap: "nowrap", background: "#00b190" }}
                    >
                      Benchmark Line Speed
                    </TableHeaderCell>
                  )}
                  {selectedColumns.lineoee && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Line OEE %
                    </TableHeaderCell>
                  )}
                  {selectedColumns.benchmarklineoee && (
                    <TableHeaderCell
                      sx={{ textWrap: "nowrap", background: "#00b190" }}
                    >
                      Benchmark Line OEE
                    </TableHeaderCell>
                  )}
                  {selectedColumns.packcap && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Pack Cap (tpw)
                    </TableHeaderCell>
                  )}
                  {isMakes &&
                    selectedColumns.actcap && (
                      <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                        Act Capacity (Min of Make & Pack)
                      </TableHeaderCell>
                    )}
                  {selectedColumns.actpackcap && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Act Pack Cap (tpw)
                    </TableHeaderCell>
                  )}
                  {selectedColumns.month && (
                    <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                      Month
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {allData
                  .filter((item) => item.capacitymonth === currentMonth)
                  .map((item, index) => (
                    <LineSpeedRow
                      key={index}
                      currentIndex={index}
                      item={item}
                      format={format}
                      allData={allData}
                      setAllData={setAllData}
                      currentProductType={currentProductType}
                      selectedColumns={selectedColumns}
                      isMakes = {isMakes}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack
            gap={2}
            direction="row"
            justifyContent="end"
            alignItems="center"
          >
            <Typography fontSize={12}>Click on save to compare</Typography>
            <Button
              size="small"
              variant="contained"
              sx={{ py: 0, heigth: 24, display: "flex", alignItems: "center" }}
              onClick={() => {
                setOeeData({
                  cu_oee: otherData.new_cu,
                  cap_increase: otherData.cap_increase,
                  months: graphData,
                });
                setAlertWidget({
                  open: true,
                  success: true,
                  message: "Check updated CU% in comparison page",
                });
                // setTableLevel("comparison");
              }}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{ py: 0, heigth: 24, display: "flex", alignItems: "center" }}
              onClick={() => {
                setOeeData({
                  cu_oee: 0,
                  cap_increase: 0,
                  months: [],
                });
                fetchData();
              }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          height: "calc(37vh - 50px)",
          pb: 2,
        }}
      >
        <Typography sx={{ width: "100%", textAlign: "center" }}>
          Demand Vs Capacity
        </Typography>
        <Box sx={{ flexGrow: 1 }}>
          <DemandCapacityGraph
            labelBg
            labels={graphData.map((item) => item.label)}
            demand={graphData.map((item) => item.demand)}
            capacity={graphData.map((item) => item.capacity)}
            cu={graphData.map((item) => item.cu)}
            threshold={thresholdLevel[currentProductType]}
          />
        </Box>
      </Paper>
    </Stack>
  );
}

// This tab component will show the prebuild summary and details data. If
// there 0 prebuild value it will redirect to the lien oee page and will
// show an alert that prebuild is not recommended.
function Prebuild({ factory, format, setTableLevel, setDisablePrebuild }) {
  const { setLoading, demandFile, runtype, runtime, setRibbonMessage,thresholdLevel,productType } =
    useContext(LTCPContext);
     let threshold =   productType !== "All"? thresholdLevel[productType]: 90

  const [result, setResult] = useState({
    output: [],
    duration: [],
    graph: { cu: [], month: [], new_cu: [] },
  });

  let option = {
    grid: {
      top: 30,
      left: 60,
      right: 60,
      bottom: 40,
    },
    title: {
      text: "CU% (DNS) vs CU% (After Prebuild)",
      left: "center",
      textStyle: {
        fontSize: 12
      },
    },
    legend: {
      data: ["CU% (DNS)", "CU% (After Prebuild)"],
      bottom: 0,
      textStyle: {
        fontSize: 10,
      },
      icon:"rect",
      itemWidth: 6,
      itemHeight: 15,
    },
    xAxis: {
      type: "category",
      data: result.graph.month,
      axisLabel: {
        fontSize: 10,
      },
    },
    yAxis: {
      type: "value",
      min: roundOffNumber(Math.min(...result.graph.cu, ...result.graph.new_cu)) - 1,
      max:
        roundOffNumber(Math.max(...result.graph.cu, ...result.graph.new_cu)) +
        1,
      axisLabel: {
        fontSize: 10,
         formatter: "{value}%"
      },
    },
    series: [
      {
        name: "CU% (DNS)",
        data: result.graph.cu,
        type: "line",
        symbol: "circle",
        symbolSize: 7,
        itemStyle: {
          color: "#FFBD65",
        },
        lineStyle: {
          color: "#FF9E00",
          width: 1,
        },
        smooth: true,
      },
      {
        name: "CU% (After Prebuild)",
        data: result.graph.new_cu,
        type: "line",
        symbol: "circle",
        symbolSize: 7,
        itemStyle: {
          color: "#1F36C7",
        },
        lineStyle: {
          color: "#1F36C7",
          width: 1,
        },
        smooth: true
      },
    ],
  };

  async function fetchData() {
    setLoading(true);
    const resp = await getRecommendationPrebuild({
      factory,
      format,
      demandFile,
      runtype,
      runtime,
    });

    const prebuildSum = resp.output.reduce((acc, curr) => {
      return acc + curr.prebuild;
    }, 0);

    if (prebuildSum === 0) {
      setLoading(false);
      setDisablePrebuild(true);
      setTableLevel("cross_sourcing");
      setRibbonMessage("Prebuild is not recommended!");
      return;
    }

    setResult(resp);
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Stack gap={2}>
      <Paper sx={{ overflowY: "auto", height: "calc(22vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5}>
          <Typography fontWeight="bold" fontSize={14}>
            Summary
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Alert Type</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Period</TableHeaderCell>
                  <TableHeaderCell>Demand (T)</TableHeaderCell>
                  <TableHeaderCell>Capacity (T)</TableHeaderCell>
                  <TableHeaderCell>CU% (DNS)</TableHeaderCell>
                  <TableHeaderCell>Potential Prebuild Qty (T)</TableHeaderCell>
                  <TableHeaderCell>CU After Prebuild</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.duration.map((item, index) => (
                  <TableRow key={index}>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.alert}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.format}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.period}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.demand)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.capacity)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu)}` } bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu >= threshold || getCUValue(item.demand, item.capacity, item.cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                      {/* {roundOffNumber(item.cu)}% */}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.prebuild)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.new_cu)}` } bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.new_cu >= threshold || getCUValue(item.demand, item.capacity, item.new_cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                      {/* {roundOffNumber(item.new_cu)}% */}
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Paper sx={{ height: "calc(38vh - 50px)" }}>
        <Stack p={2} py={0.5} gap={0.5} height="98%">
          <Typography fontWeight="bold" fontSize={14}>
            Prebuild Detail
          </Typography>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Factory</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Month</TableHeaderCell>
                  <TableHeaderCell>Demand (T)</TableHeaderCell>
                  <TableHeaderCell>Capacity (T)</TableHeaderCell>
                  <TableHeaderCell>CU% (DNS)</TableHeaderCell>
                  <TableHeaderCell>Pre build Qty (T)</TableHeaderCell>
                  <TableHeaderCell>Demand After Prebuild (T)</TableHeaderCell>
                  <TableHeaderCell>CU% After Prebuild</TableHeaderCell>
                  <TableHeaderCell>
                    Demand Above Upper CU limit (T)
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.output.map((item, index) => (
                  <TableRow key={index}>
                    <TableDataCell>{item.factory}</TableDataCell>
                    <TableDataCell>{item.format}</TableDataCell>
                    <TableDataCell>{item.month}</TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.demand)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.capacity)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {/* {roundOffNumber(item.cu)}% */}
                      <PillBox value={getCUValue(item.demand, item.capacity, item.cu)} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu >= threshold || getCUValue(item.demand, item.capacity, item.cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.prebuild)}
                    </TableDataCell>
                    <TableDataCell
                      sx={{
                        textAlign: "center",
                        fontWeight: item.prebuild > 0 ? "bold" : "regular",
                      }}
                    >
                      {roundOffNumber(item.new_demand)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      <PillBox value={getCUValue(item.demand, item.capacity, item.new_cu)} bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.new_cu >= threshold || getCUValue(item.demand, item.capacity, item.new_cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                    </TableDataCell>
                    {item.month === item.from_month && (
                      <TableDataCell
                        rowSpan={monthsBetweenInclusive(
                          item.from_month,
                          item.to_month
                        )}
                        sx={{ textAlign: "center" }}
                      >
                        {roundOffNumber(item.demand_above)}
                      </TableDataCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Paper sx={{ height: "calc(36vh - 50px)", pt: 0.2 }}>
        <ReactEChart
          option={option}
          style={{ width: "100%", height: "100%" }}
          notMerge
        />
      </Paper>
    </Stack>
  );
}

// This is the popup table where the details of the alter native factories
// will be shown in the cross sourcing page.
function CrossSourcingPopup({
  indexNum,
  factory,
  from_factory,
  format,
  onClose,
  user_email,
}) {
  const { setLoading, demandFile, runtype, runtime } = useContext(LTCPContext);

  const [result, setResult] = useState([]);

  function toOrdinal(n) {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  async function fetchData() {
    setLoading(true);

    const data = await getRecommendationCrossSourcingNested({
      factory,
      from_factory,
      format,
      demandFile,
      runtype,
      runtime,
      user_email,
    });

    setResult(data);
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <Box
      sx={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        position: "fixed",
        width: "100vw",
        height: "100vh",
        minWidth: "100vw",
        minHeight: "100vh",
        maxWidth: "100vw",
        maxHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#0008",
        zIndex: 9999999,
      }}
    >
      <Stack
        sx={{
          width: "80%",
          maxWidth: "1000px",
          height: "fit-content",
          minHeight: "fit-content",
          maxHeight: "70vh",
          background: "#fff",
        }}
      >
        <Stack
          direction="row"
          sx={{
            height: "60px",
            minHeight: "60px",
            maxHeight: "60px",
            background: "#354acd",
            justifyContent: "space-between",
            px: 2,
          }}
        >
          <Stack sx={{ height: "100%", justifyContent: "center" }}>
            <Typography fontSize={14} fontWeight="bold" color="white">
              {toOrdinal(indexNum)} Alternative Factory
            </Typography>
            <Typography fontSize={14} fontWeight="bold" color="white">
              {from_factory}
            </Typography>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{ height: "fit-content", my: "auto", color:"#FFFFFF" }}
          >
            <HighlightOffIcon />
          </IconButton>
        </Stack>
        <Box
          bgcolor="#fff"
          display="flex"
          height="98%"
          maxHeight="calc(70vh - 60px)"
        >
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Factory
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Format L2
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Month
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Demand (T)
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Capacity (T)
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    CU% DNS
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Used Capacity for cross sourcing (T)
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Demand After Allocation (T)
                  </TableHeaderCell>
                  <TableHeaderCell sx={{ textWrap: "nowrap" }}>
                    Revised CU%
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result
                  .map((item) => {
                    let month_val = item.month.split("-");
                    item.sort_key = month_val[1] + "-" + month_val[0];
                    item.month =
                      MONTH_NAME[Number.parseInt(month_val[0]) - 1] +
                      "-" +
                      month_val[1];

                    return item;
                  })
                  .sort((a, b) => a.sort_key.localeCompare(b.sort_key))
                  .map((item, index) => (
                    <TableRow key={index}>
                      <TableDataCell>{item.factory}</TableDataCell>
                      <TableDataCell>{item.format}</TableDataCell>
                      <TableDataCell>{item.month}</TableDataCell>
                      <TableDataCell>
                        {roundOffNumber(item.demand)}
                      </TableDataCell>
                      <TableDataCell>
                        {roundOffNumber(item.capacity)}
                      </TableDataCell>
                      <TableDataCell>
                        {getCUValue(item.demand, item.capacity, item.cu)}
                      </TableDataCell>
                      <TableDataCell>{roundOffNumber(item.cs)}</TableDataCell>
                      <TableDataCell>
                        {roundOffNumber(item.demand_after)}
                      </TableDataCell>
                      <TableDataCell>
                        {roundOffNumber(item.revised_cu)}%
                      </TableDataCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>
    </Box>
  );
}

// This is the cross sorucing page with summary, details and graph. It contian
// the access and reset options. If the sum of all cross sourcing value is zero
// it will redirect to the line oee page and will show an alert that cross
// sorucing is not recommended.
function CrossSourcing({ factory, format, setTableLevel, setDisableCS }) {
  const {
    setLoading,
    demandFile,
    runtype,
    runtime,
    setRibbonMessage,
    setAlertWidget,
    acceptFeature,
    thresholdLevel,
    productType
  } = useContext(LTCPContext);
  let threshold =   productType !== "All"? thresholdLevel[productType]: 90

  const { instance, accounts } = useMsal();
  const [userInfo, setUserInfo] = useState({ name: "", email: "" });
  const [activeRow, setActiveRow] = useState(factory);
  const [detailedKey, setDetailedKey] = useState(null);
  const [result, setResult] = useState({
    alter_factory: [],
    details: [],
    summary: [],
  });
  const [csRunning, setCsRunning] = useState(false);
  const [csDone, setCsDone] = useState(false);
  const [acceptRow, setAcceptRow] = useState(null);

  async function acceptCsValue(item) {
    try {
      setLoading(true);

      await acceptCS({
        demandtype: demandFile,
        runtime,
        runtype,
        factory: item.factory,
        format: item.format,
        user_email: userInfo.email,
        period: item.period,
        demand: item.demand,
        capacity: item.capacity,
        cu: item.cu,
        months: item.months,
        potential_cs: item.potential_cs,
        cu_after_cs: item.cu_after_cs,
        brand_variants: item.brand,
      });

      setLoading(false);
      setAlertWidget({
        open: true,
        success: true,
        message:
          "Recommendation is accepted. Updating the values. It may took some time to reflect on the page.",
        onClose: () => fetchData(),
      });
    } catch {
      setLoading(false);
      setAlertWidget({
        open: true,
        success: false,
        message: "Something went wrong! Please try again.",
        onClose: () => fetchData(),
      });
    }
  }

  async function resetCsValue() {
    await resetCS({
      demandtype: demandFile,
      runtime,
      runtype,
      format,
      user_email: userInfo.email,
    });

    await fetchData();
  }

  const option = {
    tooltip: {
      trigger: "axis",
    },
    legend: {
      top: "bottom",
      data: ["CU% (DNS)", "CU% (After Cross Sourcing)"],
      icon:"rect",
      itemWidth: 6,
      itemHeight: 15,
    },
    grid: {
      top: 10,
      left: 50,
      right: 50,
      bottom: 50,
    },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: result.details.map((item) => item.month),
      axisLabel: {
        interval: 0,
        fontSize: 10,
      },
    },
    yAxis: {
      type: "value",
      axisLabel: {
        fontSize: 10,
        formatter: "{value}%"
      },
    },
    series: [
      {
        name: "CU% (DNS)",
        type: "line",
        data: result.details.map((item) => roundOffNumber(item.cu)),
        symbol: "circle",
        symbolSize: 7,
        itemStyle: {
          color: "#FF9E00",
        },
        lineStyle: {
          color: "#FF9E00",
          width: 1,
        },
        smooth: true,
      },
      {
        name: "CU% (After Cross Sourcing)",
        type: "line",
        data: result.details.map((item) => roundOffNumber(item.cu_after_cs)),
        symbol: "circle",
        symbolSize: 7,
        itemStyle: {
          color: "#1F36C7",
        },
        lineStyle: {
          color: "#1F36C7",
          width: 1,
        },
        smooth: true
      },
    ],
  };

  async function fetchData() {
    setLoading(true);

    let user_email = null;

    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      user_email = account.username;
      setUserInfo({
        name: account.name || account.username || "User",
        email: account.username || account.email || "",
      });
    }

    const resp = await getRecommendationCrossSourcing({
      format,
      factory: activeRow,
      demandFile,
      runtype,
      runtime,
      user_email: user_email,
    });

    setCsRunning(resp.status === "running");
    setCsDone(resp.status ? resp.status.toLowerCase() === "done" : false);

    if (!resp.summary.some((item) => item.factory === factory)) {
      setTableLevel("line_speed");
      setDisableCS(true);
    }

    resp.summary = resp.summary.sort((a, b) => {
      if (a.factory === factory) return -1;
      else if (b.factory === factory) return 1;
      return 0;
    });

    const csSum = resp.details.reduce((acc, curr) => {
      return acc + roundOffNumber(curr.total_cs);
    }, 0);

    const summarySum = resp.summary.reduce((acc, curr) => {
      return acc + (roundOffNumber(curr.potential_cs) || 0);
    }, 0);

    if (csSum === 0) {
      setRibbonMessage("Cross Sourcing is not recommended!");
    }

    if (summarySum === 0) {
      setTableLevel("line_speed");
      setDisableCS(true);
    }

    setResult(resp);
    setLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [activeRow]);

  return (
    <Stack gap={2}>
      {csRunning && (
        <Alert severity="warning">
          Cross source acceptance is running. Please revisit this cross sourcing
          page after ~10 minutes.
        </Alert>
      )}
      {csDone && (
        <Alert severity="success">
          Cross source acceptance completed. Data has been successfully
          refreshed.
        </Alert>
      )}
      {!acceptFeature && (
        <Alert severity="warning">
          Acceptance is allowed only for the latest run
        </Alert>
      )}
      <Paper sx={{ height: "calc(42vh - 100px)" }}>
        <Stack p={2} py={0.5} gap={0.5} height="98%">
          <Typography fontWeight="bold" fontSize={14}>
            Summary
          </Typography>
          <Box sx={{ display: "flex", height: "calc(99% - 60px)" }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Alert Type
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Format L2
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Period
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Demand (T)
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Capacity (T)
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      CU%
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12 }}>
                      <Stack>
                        <Typography fontSize={10} fontWeight={500}>
                          No. of months
                        </Typography>
                        <Typography fontSize={10} fontWeight={500}>
                          {"CU > Upper Threshold"}
                        </Typography>
                      </Stack>
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12 }}>
                      <Stack>
                        <Typography fontSize={10} fontWeight={500}>
                          Potential Cross
                        </Typography>
                        <Typography fontSize={10} fontWeight={500}>
                          Source Qty (T)
                        </Typography>
                      </Stack>
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12 }}>
                      <Stack>
                        <Typography fontSize={10} fontWeight={500}>
                          CU After
                        </Typography>
                        <Typography fontSize={10} fontWeight={500}>
                          Cross Sourcing
                        </Typography>
                      </Stack>
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12 }}>
                      <Stack>
                        <Typography fontSize={10} fontWeight={500}>
                          Brand Variants
                        </Typography>
                        <Typography fontSize={10} fontWeight={500}>
                          Produced
                        </Typography>
                      </Stack>
                    </TableHeaderCell>
                    <TableHeaderCell sx={{ fontSize: 12, textAlign: "center" }}>
                      Accept Status
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.summary.map((item, index) => (
                    <TableRow
                      key={index}
                      onClick={() => setActiveRow(item.factory)}
                    >
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {item.factory}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {item.format}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {item.period}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {roundOffNumber(item.demand)}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {roundOffNumber(item.capacity)}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{
                          textAlign: "center",
                          // background: item.cu > 100 ? "#FDECED" : "#fff",
                        }}
                      >
                        {getCUValue(item.demand, item.capacity, item.cu)}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {item.months}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {roundOffNumber(item.potential_cs)}
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{
                          textAlign: "center",
                          // background: item.cu_after_cs > 100 ? "#FDECED" : "#fff",
                        }}
                      >
                        {roundOffNumber(item.cu_after_cs)}%
                      </ToggleCell>
                      <ToggleCell
                        active={item.factory === activeRow}
                        removed={!item.from_df1}
                        sx={{ textAlign: "center" }}
                      >
                        {item.brand}
                      </ToggleCell>
                      <TableDataCell sx={{ p: 0 }}>
                        <Button
                          size="small"
                          variant="contained"
                          sx={{
                            p: 0,
                            px: 2,
                            width: "100%",
                            ...(item.accepted
                              ? { background: "#A3E1D2", color: "#000" }
                              : {
                                background: "#455be5" 
                              }),
                          }}
                          endIcon={<CheckCircleOutlineIcon />}
                          disabled={
                            !item.accepted &&
                            (!acceptFeature ||
                              csRunning ||
                              item.factory !== activeRow)
                          }
                          onClick={() => !item.accepted && setAcceptRow(item)}
                        >
                          {item.accepted
                            ? `Accepted ${item.sequence}`
                            : "Accept"}
                        </Button>
                      </TableDataCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Stack
            mt="auto"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography fontSize={9} color="red">
              Note: On Accepting Recommendation of one factory demand numbers of
              other factory gets updated.
            </Typography>
            <Button
              size="small"
              sx={{ py: 0.5 }}
              onClick={resetCsValue}
              startIcon={<LockResetIcon fontSize="small" />}
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </Paper>
      <Stack direction="row" gap={2} maxWidth="100%" overflow="auto">
        <Box
          sx={{
            width: "218px",
            minWidth: "218px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "4px",
            background: "linear-gradient(to right, #455be5, #596de9)",
          }}
        >
          <Typography fontSize={14} fontWeight="bold" color="#fff">
            Alternate Factories
          </Typography>
        </Box>

        {result.alter_factory.map((item, index) => (
          <CrossSourcingCard
            key={index}
            allData={item}
            indexNum={index + 1}
            text={item.factory}
            onClick={() =>
              setDetailedKey({
                indexNum: index + 1,
                factory: activeRow,
                from_factory: item.factory,
                format,
              })
            }
          />
        ))}
      </Stack>
      <Paper sx={{ height: "calc(53vh - 100px)" }}>
        <Stack sx={{ p: 2, py: 0.5, gap: 0.5 }} height="98%">
          <Typography fontWeight="bold" fontSize={14}>
            Cross Sourcing Details
          </Typography>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Factory</TableHeaderCell>
                  <TableHeaderCell>Format L2</TableHeaderCell>
                  <TableHeaderCell>Month</TableHeaderCell>
                  <TableHeaderCell>Demand (T)</TableHeaderCell>
                  <TableHeaderCell>Capacity (T)</TableHeaderCell>
                  <TableHeaderCell>CU% (DNS)</TableHeaderCell>
                  <TableHeaderCell>
                    <Stack>
                      <Typography fontSize={10} fontWeight={500}>
                        Demand Before
                      </Typography>
                      <Typography fontSize={10} fontWeight={500}>
                        Cross Sourcing (T)
                      </Typography>
                    </Stack>
                  </TableHeaderCell>
                  <TableHeaderCell>
                    <Stack>
                      <Typography fontSize={10} fontWeight={500}>
                        CU% Before
                      </Typography>
                      <Typography fontSize={10} fontWeight={500}>
                        Cross Sourcing
                      </Typography>
                    </Stack>
                  </TableHeaderCell>
                  <TableHeaderCell>Cross Sourced Qty (T)</TableHeaderCell>
                  <TableHeaderCell>
                    <Stack>
                      <Typography fontSize={10} fontWeight={500}>
                        CU% After
                      </Typography>
                      <Typography fontSize={10} fontWeight={500}>
                        Cross Sourcing
                      </Typography>
                    </Stack>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.details.map((item, index) => (
                  <TableRow key={index}>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.factory}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.format}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {item.month}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.demand)}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.capacity)}
                    </TableDataCell>
                    <TableDataCell
                      sx={{
                        textAlign: "center",
                        // background: item.cu > 100 ? "#FDECED" : "#fff",
                      }}
                    >
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu)}` } bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu >= threshold || getCUValue(item.demand, item.capacity, item.cu) === "No Capacity" )?"#ff9998":"White"}`}/>
                      {/* {getCUValue(item.demand, item.capacity, item.cu)} */}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.new_demand)}
                    </TableDataCell>
                    <TableDataCell
                      sx={{
                        textAlign: "center",
                        // background: item.cu_apb > 100 ? "#FDECED" : "#fff",
                      }}
                    >
                      <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_apb)}` } bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_apb >= threshold || getCUValue(item.demand, item.capacity, item.cu_apb) === "No Capacity" )?"#ff9998":"White"}`}/>
                      {/* {getCUValue(item.demand, item.capacity, item.cu_apb)} */}
                    </TableDataCell>
                    <TableDataCell sx={{ textAlign: "center" }}>
                      {roundOffNumber(item.total_cs)}
                    </TableDataCell>
                    <TableDataCell
                      sx={{
                        textAlign: "center",
                        // background: item.cu_after_cs > 100 ? "#FDECED" : "#fff",
                      }}
                    >
                       <PillBox value={`${getCUValue(item.demand, item.capacity, item.cu_after_cs)}` } bgColor = { `${runtype.toLowerCase() !== "schedule" && (item.cu_after_cs >= threshold || getCUValue(item.demand, item.capacity, item.cu_after_cs) === "No Capacity" )?"#ff9998":"White"}`}/>
                      {/* {getCUValue(item.demand, item.capacity, item.cu_after_cs)} */}
                    </TableDataCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
      <Paper
        sx={{
          heigth: "40vh",
          minHeight: "40vh",
          maxHeight: "40vh",
        }}
      >
        <Stack sx={{ alignItems: "center", height: "100%" }}>
          <Typography>CU% (DNS) vs CU% (After Cross Sourcing)</Typography>
          <Box sx={{ height: "100%", width: "100%" }}>
            <ReactEChart
              style={{ height: "100%", width: "100%" }}
              option={option}
              notMerge
            />
          </Box>
        </Stack>
      </Paper>
      {detailedKey && (
        <CrossSourcingPopup
          format={detailedKey.format}
          factory={detailedKey.factory}
          indexNum={detailedKey.indexNum}
          from_factory={detailedKey.from_factory}
          user_email={userInfo.email}
          onClose={() => setDetailedKey(null)}
        />
      )}
      <Dialog
        open={Boolean(acceptRow)}
        fullWidth
        maxWidth="xs"
        onClose={() => setAcceptRow(null)}
      >
        <DialogContent>
          <Typography>Do you want to accept this recommendation?</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              acceptCsValue(acceptRow);
              setAcceptRow(null);
            }}
          >
            Accept
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => setAcceptRow(null)}
          >
            Decline
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

// This is the page component of recommendation page where all the tabs are
// present: prebuild, cross sourcing, line oee and comparison. This contain
// some parent level state that we will use accross multiple tabs.
function Recommendation() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { factory, format } = useParams();
  const [tableLevel, setTableLevel] = useState("prebuild");
  const [disablePrebuild, setDisablePrebuild] = useState(false);
  const [disableCS, setDisableCS] = useState(false);
  const { runtime, runtype } = useContext(LTCPContext);
  const [showInfo, setShowInfo] = useState(false);
  const [oeeData, setOeeData] = useState({
    cu_oee: 0,
    cap_increase: 0,
    months: [],
  });

  function resetOeeData() {
    setOeeData({
      cu_oee: 0,
      cap_increase: 0,
      months: [],
    });
  }

  useEffect(() => {
    if (!runtime || !runtype) return navigate(ltcpRoutes.LANDING);
  }, []);

  return (
    <Stack px={4} py={2} gap={2} flexGrow={1}>
      <Stack direction="row" gap={2} alignItems="center" height={40}>
        <IconButton
          onClick={() => {
            navigate(ltcpRoutes.ALERTS, {
              state: state ? { ...state, tab: "factory" } : {},
            });
          }}
        >
          <KeyboardBackspaceIcon />
        </IconButton>
        <Typography fontWeight="bold" fontSize={21}>
          Recommendation
        </Typography>
        <Stack
          direction="row"
          gap={1}
          flexGrow={1}
          maxWidth="600px"
          borderBottom="1px solid #ccc"
        >
          <Typography
            p={0.5}
            flexGrow={1}
            fontSize={14}
            textAlign="center"
            onClick={() => {
              if (!disablePrebuild) {
                setTableLevel("prebuild");
                resetOeeData();
              }
            }}
            borderBottom={
              tableLevel === "prebuild" ? "2px solid #1F36C7" : "none"
            }
            sx={{
              cursor: "pointer",
              color: disablePrebuild ? "#bbb" : "inherit",
            }}
          >
            Prebuild
          </Typography>
          <Typography
            p={0.5}
            flexGrow={1}
            fontSize={14}
            textAlign="center"
            onClick={() => {
              if (!disableCS) {
                setTableLevel("cross_sourcing");
                resetOeeData();
              }
            }}
            borderBottom={
              tableLevel === "cross_sourcing" ? "2px solid #1F36C7" : "none"
            }
            sx={{
              cursor: "pointer",
              color: disableCS ? "#bbb" : "inherit",
            }}
          >
            Cross Sourcing
          </Typography>
          <Typography
            p={0.5}
            flexGrow={1}
            fontSize={14}
            textAlign="center"
            onClick={() => {
              setTableLevel("line_speed");
              resetOeeData();
            }}
            borderBottom={
              tableLevel === "line_speed" ? "2px solid #1F36C7" : "none"
            }
            sx={{ cursor: "pointer" }}
          >
            Line Speed/OEE Increase
          </Typography>
          <Typography
            p={0.5}
            flexGrow={1}
            fontSize={14}
            textAlign="center"
            onClick={() => setTableLevel("comparison")}
            borderBottom={
              tableLevel === "comparison" ? "2px solid #1F36C7" : "none"
            }
            sx={{ cursor: "pointer" }}
          >
            Comparison
          </Typography>
        </Stack>
        {tableLevel !== "comparison" && (
          <IconButton onClick={() => setShowInfo(true)}>
            <InfoOutlineIcon />
          </IconButton>
        )}
      </Stack>
      {tableLevel === "prebuild" && (
        <Prebuild
          factory={factory}
          format={format}
          setTableLevel={setTableLevel}
          setDisablePrebuild={setDisablePrebuild}
        />
      )}
      {tableLevel === "line_speed" && (
        <LineSpeed
          factory={factory}
          format={format}
          setOeeData={setOeeData}
          setTableLevel={setTableLevel}
        />
      )}
      {tableLevel === "cross_sourcing" && (
        <CrossSourcing
          factory={factory}
          format={format}
          setTableLevel={setTableLevel}
          setDisableCS={setDisableCS}
        />
      )}
      {tableLevel === "comparison" && (
        <Comparison factory={factory} format={format} oeeData={oeeData} />
      )}
      <Drawer open={showInfo} anchor="right" onClose={() => setShowInfo(false)}>
        {tableLevel === "prebuild" && (
          <Stack width={800} alignItems="stretch">
            <Stack
              flexDirection="row"
              sx={{
                maxWidth: 800,
                height: 48,
                width: "100%",
                background: theme.palette.primary.main,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                fontSize={14}
                fontWeight="bold"
                color="white"
                sx={{ pl: 2 }}
              >
                Prebuild Logic
              </Typography>
              <IconButton
                size="small"
                sx={{ ml: 2 }}
                onClick={() => setShowInfo(false)}
              >
                <CloseIcon fontSize="small" sx={{ color: "#ccc" }} />
              </IconButton>
            </Stack>
            <Stack p={3} background="#f6f7ff">
              <Typography fontSize={12} fontWeight="bold">
                Prebuild
              </Typography>
              <Typography fontSize={11}>
                Prebuild Recommendation helps plan production in advance when
                capacity is expected to exceed safe limits. Here's how it works:
              </Typography>
              <Typography fontSize={11} sx={{ mt: 3, mb: 1 }} fontWeight="bold">
                Note:
              </Typography>
              <Stack sx={{ width: "100%", gap: 1 }}>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Triggered when Capacity Utilization (CU%) goes over the set
                  threshold (lower threshold) in any rolling month.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Thresholds are defined at Sub-Category level in the Alerts &
                  Recommendations Master.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Demand above the threshold is identified at the month level.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  System checks earlier months with available capacity (CU%
                  below the threshold) to prebuild that demand.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Prebuilding is done for the number of months specified in the
                  Alerts and Recommendation Master data.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Prebuild stops if an earlier month's CU% also exceeds the
                  threshold.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Demand after prebuild is used to generate cross-sourcing
                  recommendations.
                </Typography>
              </Stack>
              <Typography fontSize={11} sx={{ mt: 3, mb: 1 }} fontWeight="bold">
                Step to identify cross source quantity:
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <InfoTableHeaderCell>Step</InfoTableHeaderCell>
                      <InfoTableHeaderCell>Description</InfoTableHeaderCell>
                      <InfoTableHeaderCell>Formulas</InfoTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <InfoTableDataCell>1</InfoTableDataCell>
                      <InfoTableDataCell>
                        Identify “Available capacity” and “Demand above
                        Threshold” for a Factory-Format L2 combination at month
                        level.
                      </InfoTableDataCell>
                      <InfoTableDataCell>
                        Available capacity = (Threshold CU x Capacity x OR) -
                        Demand
                        <br />
                        Demand above Threshold = (CU – Threshold CU) x Capacity
                        x OR
                      </InfoTableDataCell>
                    </TableRow>
                    <TableRow>
                      <InfoTableDataCell>2</InfoTableDataCell>
                      <InfoTableDataCell>
                        Identify “Prebuild qty” based on demand above threshold
                        and previous months capacity.
                      </InfoTableDataCell>
                      <InfoTableDataCell sx={{ textAlign: "center" }}>
                        NA
                      </InfoTableDataCell>
                    </TableRow>
                    <TableRow>
                      <InfoTableDataCell>3</InfoTableDataCell>
                      <InfoTableDataCell>
                        Identify “demand after prebuild” and “CU after prebuild”
                        based on the prebuild qty identified.
                      </InfoTableDataCell>
                      <InfoTableDataCell>
                        (For Prebuild month)
                        <br />
                        Demand after Prebuild = Prebuild qty + Demand
                        <br />
                        (For Other months)
                        <br />
                        Demand after Prebuild = Demand - (Ratio of its demand
                        with total demand) x Prebuild qty <br />
                        CU After Prebuild = Demand After Prebuild/ (Capacity x
                        OR)
                      </InfoTableDataCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        )}
        {tableLevel === "cross_sourcing" && (
          <Stack width={800} alignItems="stretch">
            <Stack
              flexDirection="row"
              sx={{
                maxWidth: 800,
                height: 48,
                width: "100%",
                background: theme.palette.primary.main,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                fontSize={14}
                fontWeight="bold"
                color="white"
                sx={{ pl: 2 }}
              >
                Cross Sourcing Logic
              </Typography>
              <IconButton
                size="small"
                sx={{ ml: 2 }}
                onClick={() => setShowInfo(false)}
              >
                <CloseIcon fontSize="small" sx={{ color: "#ccc" }} />
              </IconButton>
            </Stack>
            <Stack p={3} background="#f6f7ff">
              <Typography fontSize={12} fontWeight="bold">
                Cross Sourcing
              </Typography>
              <Typography fontSize={11}>
                Cross Sourcing Recommendation helps manage excess demand by
                distributing it to other nearby factories when one factory
                cannot handle it, even after prebuilding.
              </Typography>
              <Typography fontSize={11} sx={{ mt: 3, mb: 1 }} fontWeight="bold">
                Note:
              </Typography>
              <Stack sx={{ width: "100%", gap: 1 }}>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Triggered when Capacity Utilization (CU%), after Prebuild,
                  exceeds the upper threshold in any rolling month.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Thresholds are defined at Sub-Category level in the Alerts &
                  Recommendations Master.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Uses ‘Demand After Prebuild’ as the input. If prebuild doesn’t
                  apply, the tool uses its direct demand output for generating
                  Cross sourcing.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Demand above Upper Threshold is considered for cross sourcing
                  from alternative factories.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  The system checks for alternate factories, starting with the
                  nearest one based on postal code distance and allocates demand
                  based on available capacity.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Alternative factory is considered only if its available
                  monthly capacity is ≥ 20 tonnes.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  If one factory can’t meet the demand, the system checks the
                  next closest factory and allocates demand and so on.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  This continues until: All excess demand is covered, or ll
                  alternative factories are fully utilized for that month.
                </Typography>
              </Stack>
              <Typography fontSize={11} sx={{ mt: 3, mb: 1 }} fontWeight="bold">
                Step to identify cross source quantity:
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <InfoTableHeaderCell>Step</InfoTableHeaderCell>
                      <InfoTableHeaderCell>Description</InfoTableHeaderCell>
                      <InfoTableHeaderCell>Formulas</InfoTableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <InfoTableDataCell>1</InfoTableDataCell>
                      <InfoTableDataCell>
                        Identify “demand above upper threshold CU” from “CU
                        before cross sourcing” column at month level.
                      </InfoTableDataCell>
                      <InfoTableDataCell>
                        Demand above Upper Threshold CU = (CU before cross
                        sourcing - Upper Threshold CU) x Capacity x OR
                      </InfoTableDataCell>
                    </TableRow>
                    <TableRow>
                      <InfoTableDataCell>2</InfoTableDataCell>
                      <InfoTableDataCell>
                        Cross source the “demand above upper threshold CU” qty
                        identified from the nearest factory at month level based
                        on the alternative factory’s available capacity.
                      </InfoTableDataCell>
                      <InfoTableDataCell sx={{ textAlign: "center" }}>
                        NA
                      </InfoTableDataCell>
                    </TableRow>
                    <TableRow>
                      <InfoTableDataCell>3</InfoTableDataCell>
                      <InfoTableDataCell>
                        “Cross sourced qty” is identified by aggregating the
                        cross sourced qty from all the factories at month level
                      </InfoTableDataCell>
                      <InfoTableDataCell>
                        Cross sourced qty = Cross sourced qty from 1st nearest
                        factory + Cross sourced qty from 2nd nearest factory +
                        ……
                      </InfoTableDataCell>
                    </TableRow>
                    <TableRow>
                      <InfoTableDataCell>4</InfoTableDataCell>
                      <InfoTableDataCell>
                        Identify “demand after cross sourcing” and “CU after
                        cross sourcing” based on the “Cross sourced qty”
                        identified at month level.
                      </InfoTableDataCell>
                      <InfoTableDataCell>
                        Demand after cross sourcing = (Demand before cross
                        sourcing - Cross sourced qty)
                        <br />
                        CU after cross sourcing = (Demand after cross sourcing -
                        Cross sourced qty)/(Capacity x OR)
                      </InfoTableDataCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Stack>
        )}
        {tableLevel === "line_speed" && (
          <Stack width={800} alignItems="stretch">
            <Stack
              flexDirection="row"
              sx={{
                maxWidth: 800,
                height: 48,
                width: "100%",
                background: theme.palette.primary.main,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                fontSize={14}
                fontWeight="bold"
                color="white"
                sx={{ pl: 2 }}
              >
                Line Speed/OEE Logic
              </Typography>
              <IconButton
                size="small"
                sx={{ ml: 2 }}
                onClick={() => setShowInfo(false)}
              >
                <CloseIcon fontSize="small" sx={{ color: "#ccc" }} />
              </IconButton>
            </Stack>
            <Stack p={3} background="#f6f7ff">
              <Typography fontSize={12} fontWeight="bold">
                Line Speed/OEE Increase
              </Typography>
              <Typography fontSize={11}>
                Line Speed and OEE Recommendation helps improve factory
                performance by benchmarking key metrics like Batch cycle time,
                Line speed and Line OEE.
              </Typography>
              <Stack sx={{ width: "100%", mt: 3, gap: 1 }}>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  This is an independent recommendation - it doesn't rely on
                  Prebuild or Cross Sourcing outputs.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Shows factory-wise details, including assigned make and pack
                  shifts.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Benchmark BCT (Batch Cycle Time) is calculated by comparing
                  Brand Variant and Batch Size across all factories making the
                  same item. The lowest BCT among them is set as the benchmark.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Line Speed and OEE Benchmark is determined by comparing:
                  Format L2, Machine and Grammage across factories producing
                  similar products. The Highest Line speed and Line OEE among
                  them is set as the benchmark.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  On the front end, users can edit BCT, Line Speed and Line OEE
                  to simulate how those changes affect production capacity.
                </Typography>
              </Stack>
              <Typography fontSize={11} sx={{ mt: 3, mb: 1 }} fontWeight="bold">
                Note:
              </Typography>
              <Stack sx={{ width: "100%", gap: 1 }}>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  BCT, Line Speed and Line OEE are editable fields for the
                  recommended Factory-Format L2 combination.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Editing these fields will update the capacity numbers in table
                  and graph based on the input values.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  After editing these fields, Increase in Capacity is reflected
                  under Potential capacity Increase column.
                </Typography>
                <Typography fontSize={10}>
                  <PlayArrowIcon
                    sx={{
                      fontSize: 10,
                      color: theme.palette.primary.main,
                      mr: 1,
                    }}
                  />
                  Saving the changes will be reflected under “CU After Line
                  speed/OEE Increase” column in the comparison page.
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

export default Recommendation;
